import { useGenerateSiteLinkMutation } from '@/shared/api'
import { SiteLinkWithAnalyticsType } from '@/shared/api/site-links-group/site-links-group.types'
import { InviteSVGIcon, PlusSVGIcon } from '@/shared/assets'
import { Button, Input, LoadingIndicator } from '@/shared/ui'
import { UtmBuilderField } from '@/shared/ui/utm-builder-field/UtmBuilderField'
import {
    UseUtmArgs,
    useUtmIdSelect,
} from '@/shared/ui/utm-builder-field/utmIdSelect'
import { Site, useActiveUser } from '@/true-fsd/data/advertiser-account'
import { useActiveWorkspace } from '@/true-fsd/data/advertiser-account/hooks'
import { siteMethods } from '@synapex/shared2/entities'
import { AxiosError } from 'axios'
import clsx from 'clsx'
import { CSSProperties, useCallback, useState } from 'react'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import { generateLinkSchemaSites, useGenerateLinkValidation } from '../hooks'
import { SpecialInput } from '../special-input'
import './createInviteLink.scss'
import { capitalize } from '@synapex/shared2/utils/string'

const UTM_BUILDER_STYLE = {
    header: {
        height: 36,
    },
} satisfies Record<string, CSSProperties>

const useUtmIdSelectById = ({ key, site_id }: Omit<UseUtmArgs, 'stateBy'>) => {
    const [selectedId, onSelectedIdChange] = useState('')

    return useUtmIdSelect({
        key,
        site_id,
        stateBy: {
            type: 'id',
            selectedId,
            onSelectedIdChange,
        },
    })
}

interface GenerateLinkData {
    linkName: string
    budget: number
    siteName: string | null
}

interface CreateInviteLinkProps {
    activeSite: Site

    onInviteLinkCreated?: (newLink: SiteLinkWithAnalyticsType) => void
    onInviteLinkUpdated?: (updatedLink: SiteLinkWithAnalyticsType) => void
    onRemoveInviteLink?: (newLink: SiteLinkWithAnalyticsType) => void
}

export const CreateSiteInviteLink = ({
    activeSite,
    onInviteLinkCreated,
    onInviteLinkUpdated,
    onRemoveInviteLink,
}: CreateInviteLinkProps) => {
    const { groupId } = useParams<{ groupId: string }>()
    const numericGroupId = groupId ? Number(groupId) : 0

    const user = useActiveUser()
    const { subscription } = useActiveWorkspace()

    const activeSiteId = activeSite.site_id

    const { isValid, errors, validate } =
        useGenerateLinkValidation<GenerateLinkData>(generateLinkSchemaSites)

    const generateInviteLink = useGenerateSiteLinkMutation()

    const source = useUtmIdSelectById({ key: 'source', site_id: activeSiteId })
    const medium = useUtmIdSelectById({ key: 'medium', site_id: activeSiteId })
    const content = useUtmIdSelectById({
        key: 'content',
        site_id: activeSiteId,
    })
    const campaign = useUtmIdSelectById({
        key: 'campaign',
        site_id: activeSiteId,
    })

    const [generateLinkData, setGenerateLinkData] = useState<GenerateLinkData>({
        linkName: '',
        budget: 0,
        siteName: null,
    })

    const setLinkName = useCallback((value: string) => {
        setGenerateLinkData((prev) => ({ ...prev, linkName: value }))
    }, [])

    const setLinkBudget = useCallback((value: number) => {
        setGenerateLinkData((prev) => ({ ...prev, budget: value }))
    }, [])

    const setSiteName = useCallback((name: string | null) => {
        setGenerateLinkData((prev) => ({ ...prev, siteName: name }))
    }, [])

    const handleBudgetChange = useCallback(
        (value: string) => {
            const budget = parseFloat(value)
            setLinkBudget(isNaN(budget) ? 0 : budget)
        },
        [setLinkBudget]
    )

    const [isCreating, setIsCreating] = useState(false)

    const createSiteLink = async () => {
        const { linkName, budget, siteName } = generateLinkData

        // Optimistic UI: Create a temporary invite link object
        const tempInviteLink: SiteLinkWithAnalyticsType = {
            budget,
            status: 'no_status', // Status to indicate pending creation
            created_at: new Date(),
            user_id: user.user_id || activeSite.user_id, // Add missing property
            site_id: activeSite.site_id, // Add missing property
            site_link_id: 0, // Add missing property (can be set later)
            site_link_name: (linkName || siteName) ?? '',
            site_links_group_id: 0,
            visits: null,
            users: null,
            leads: null,
            cpv: null,
            cpu: null,
            cpl: null,
            publishing_duration: null,
            published_at: null,
            updated_at: new Date(),
            deleted_at: null,
            cpm: null,
            external_channel_id: null,
            utmLink: {
                baseUrl: siteMethods.getTitleString(activeSite),
                utm_link_id: 0,
                siteLink_id: 0,
                source_id: null,
                medium_id: null,
                campaign_id: null,
                content_id: null,
            },
            active_creative: null,
            active_creative_id: null,
            siteLinkUrl: null,
            analytics: [],
            publisher: null,
            external_channel: null,
        }

        // Notify parent component (InviteLinksWidget) to add this link to the table
        if (onInviteLinkCreated) {
            onInviteLinkCreated(tempInviteLink)
        }

        setIsCreating(true)
        const result = await generateInviteLink.mutateAsync(
            {
                siteId: activeSite?.site_id ?? 0,
                userId: activeSite?.user_id ?? 0,
                name: (linkName || siteName) ?? '',
                budget: budget,
                groupId: numericGroupId,
                creatorUserId: activeSite?.user_id ?? 0,
                mediumId: medium.value == '' ? undefined : Number(medium.value),
                campaignId:
                    campaign.value == '' ? undefined : Number(campaign.value),
                contentId:
                    content.value == '' ? undefined : Number(content.value),
                sourceId: source.value == '' ? undefined : Number(source.value),
            },
            {
                onSettled: async (data, error) => {
                    setIsCreating(false)
                    if (error) {
                        console.error(error)
                    }
                    if (data) {
                        // Update the temporary invite link with the actual URL from the backend
                        // Similarly for onInviteLinkUpdated
                        if (onInviteLinkCreated) {
                            onInviteLinkCreated({ ...data })
                        }
                        if (onInviteLinkUpdated) {
                            onInviteLinkUpdated({ ...data }) //
                        }
                    }
                },
                onError: async (data) => {
                    if (onRemoveInviteLink) {
                        onRemoveInviteLink(tempInviteLink)
                    }

                    toast.error(
                        (
                            (data as AxiosError).response?.data as Record<
                                string,
                                string
                            >
                        )?.message
                    )
                },
            }
        )

        if (result) {
            return true
        }
    }

    const generateLinkHandler = async () => {
        if (!groupId) return

        const isValid = validate(generateLinkData)
        if (!isValid) {
            return
        }

        try {
            if (await createSiteLink()) {
                // if invite link is created successfully
                resetLinkData()
            }
        } catch (error) {
            console.error('Failed to generate invite link:', error)
        }
    }

    function resetLinkData() {
        setGenerateLinkData({ linkName: '', budget: 0, siteName: null })

        for (const utm of [source, medium, campaign, content]) {
            utm.onChange('')
        }
    }

    const forbidToCreateGroups = subscription.status === 'expired'

    return (
        <div className="create-invite-link w-full gap-2">
            <div className="create-invite-link__icon">
                <InviteSVGIcon />
            </div>
            <SpecialInput
                title={null}
                className="special-input w-[216px]"
                channelName={generateLinkData.siteName}
                channelId={activeSite?.site_id ?? 0}
                value={generateLinkData.linkName}
                onChange={setLinkName}
                placeholder="Введите @название канала"
                onChannelNameChange={setSiteName}
                hasError={!!errors.linkName}
            />
            <Input
                className="w-[180px]"
                placeholder="Укажите бюджет"
                value={generateLinkData.budget || ''}
                onChange={handleBudgetChange}
            />
            {[source, medium, campaign, content].map((item) => (
                <UtmBuilderField
                    key={item.key}
                    headerStyle={UTM_BUILDER_STYLE.header}
                    createEntityHandler={item.create}
                    items={item.query.data ?? []}
                    fieldTitle={capitalize(item.key)}
                    placeholder={capitalize(item.key)}
                    selectedId={item.value}
                    inputValue={item.newNameInput}
                    isLoading={item.query.isLoading}
                    setInputValue={item.setNewNameInput}
                    onSelectedIdChange={item.onChange}
                />
            ))}
            <Button
                onClick={generateLinkHandler}
                size="fit"
                disabled={!isValid}
                disabledBySubscriptionStatus={forbidToCreateGroups}
                className={clsx(`create-invite-link__button w-fit truncate`, {
                    'create-invite-link__button--disabled':
                        !isValid || forbidToCreateGroups,
                })}
                tooltipStyle="right-[calc(100%+3px)] left-auto"
                wrapperClassName="create-invite-link__button-container"
                tooltipText="Чтобы создавать новые ссылки, оплатите подписку на сервис"
            >
                {isCreating ? (
                    <LoadingIndicator
                        style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '0.5em',
                        }}
                    />
                ) : (
                    <PlusSVGIcon
                        className={clsx(`button__icon-left flex-shrink-0`, {
                            'button__icon--disabled':
                                !isValid || forbidToCreateGroups,
                        })}
                    />
                )}
                <p className="truncate">Создать ссылку</p>
            </Button>
        </div>
    )
}
